/* =========================================================================
   PAMPA Samples Page — shared behaviour
   - <pampa-samples-grid>  static-block filter engine + wishlist delegation
   - <pampa-sample-card>   material -> real variant resolution for one card
   Both are custom elements, so Shopify Theme Editor section/block reloads
   (which replace the section's markup) automatically get fresh, correctly
   scoped listeners with no manual re-init bookkeeping and no duplicate
   listeners — each DOM node only ever runs its own connectedCallback once.
   ========================================================================= */

(function () {
  'use strict';

  function filterDataKey(key) {
    return 'filter' + key.charAt(0).toUpperCase() + key.slice(1);
  }

  function readWishlist() {
    try {
      var raw = window.localStorage.getItem('pampa_wishlist');
      var parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  function writeWishlist(list) {
    try {
      window.localStorage.setItem('pampa_wishlist', JSON.stringify(list));
    } catch (e) {
      /* localStorage unavailable — wishlist just won't persist; cart is unaffected */
    }
  }

  /* ---------------------------------------------------------------------
     <pampa-samples-grid>
     --------------------------------------------------------------------- */

  if (!window.customElements.get('pampa-samples-grid')) {
    window.customElements.define(
      'pampa-samples-grid',
      class PampaSamplesGrid extends HTMLElement {
        connectedCallback() {
          this.cards = Array.prototype.slice.call(this.querySelectorAll('.pampa-samples-card'));
          this.filterBar = this.querySelector('.pampa-samples-filters');
          this.emptyState = this.querySelector('.pampa-samples-grid__empty');
          this.resetBtn = this.querySelector('.pampa-samples-filters__reset');
          this.searchInput = this.querySelector('[data-filter-search-input]');
          this.selects = this.filterBar
            ? Array.prototype.slice.call(this.filterBar.querySelectorAll('select[data-filter]'))
            : [];

          if (this.filterBar) {
            this.buildFilterOptions();
            this.bindFilterEvents();
          }

          this.addEventListener('click', this.handleWishlistClick.bind(this));
          this.hydrateWishlistState();
        }

        buildFilterOptions() {
          var cards = this.cards;
          this.selects.forEach(function (select) {
            var key = select.getAttribute('data-filter');
            var dataKey = filterDataKey(key);
            var values = [];
            cards.forEach(function (card) {
              var raw = card.dataset[dataKey];
              if (!raw) return;
              raw
                .split(',')
                .map(function (v) {
                  return v.trim();
                })
                .filter(Boolean)
                .forEach(function (v) {
                  if (values.indexOf(v) === -1) values.push(v);
                });
            });
            values.sort(function (a, b) {
              return a.localeCompare(b);
            });

            var allLabel = select.getAttribute('data-all-label') || 'All';
            var currentValue = select.value;
            select.innerHTML = '';

            var optAll = document.createElement('option');
            optAll.value = '';
            optAll.textContent = allLabel;
            select.appendChild(optAll);

            values.forEach(function (v) {
              var opt = document.createElement('option');
              opt.value = v;
              opt.textContent = v;
              select.appendChild(opt);
            });

            if (values.indexOf(currentValue) !== -1) select.value = currentValue;
          });
        }

        bindFilterEvents() {
          var apply = this.applyFilters.bind(this);
          this.selects.forEach(function (select) {
            select.addEventListener('change', apply);
          });

          if (this.searchInput) {
            var timer;
            this.searchInput.addEventListener('input', function () {
              window.clearTimeout(timer);
              timer = window.setTimeout(apply, 150);
            });
          }

          if (this.resetBtn) {
            this.resetBtn.addEventListener('click', this.resetFilters.bind(this));
          }
        }

        applyFilters() {
          var active = {};
          this.selects.forEach(function (select) {
            if (select.value) active[select.getAttribute('data-filter')] = select.value.toLowerCase();
          });
          var query = this.searchInput ? this.searchInput.value.trim().toLowerCase() : '';
          var visibleCount = 0;

          this.cards.forEach(function (card) {
            var matches = true;

            for (var key in active) {
              if (!Object.prototype.hasOwnProperty.call(active, key)) continue;
              var cardValue = (card.dataset[filterDataKey(key)] || '').toLowerCase();
              if (cardValue !== active[key]) {
                matches = false;
                break;
              }
            }

            if (matches && query) {
              var haystack = (card.dataset.filterSearch || '').toLowerCase();
              if (haystack.indexOf(query) === -1) matches = false;
            }

            card.hidden = !matches;
            if (matches) visibleCount++;
          });

          if (this.emptyState) this.emptyState.classList.toggle('is-visible', visibleCount === 0);
        }

        resetFilters() {
          this.selects.forEach(function (select) {
            select.value = '';
          });
          if (this.searchInput) this.searchInput.value = '';
          this.applyFilters();
        }

        handleWishlistClick(evt) {
          var button = evt.target.closest ? evt.target.closest('.pampa-samples-card__wishlist') : null;
          if (!button || !this.contains(button)) return;

          var id = button.getAttribute('data-wishlist-id');
          if (!id) return;

          var list = readWishlist();
          var index = list.indexOf(id);
          var isActive;

          if (index === -1) {
            list.push(id);
            isActive = true;
          } else {
            list.splice(index, 1);
            isActive = false;
          }

          writeWishlist(list);
          button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        }

        hydrateWishlistState() {
          var list = readWishlist();
          if (!list.length) return;
          var buttons = this.querySelectorAll('.pampa-samples-card__wishlist');
          buttons.forEach(function (button) {
            var id = button.getAttribute('data-wishlist-id');
            if (id && list.indexOf(id) !== -1) button.setAttribute('aria-pressed', 'true');
          });
        }
      }
    );
  }

  /* ---------------------------------------------------------------------
     <pampa-sample-card>
     Resolves the real Shopify variant for the selected material and keeps
     the hidden add-to-cart form, submit button and price display in sync.
     Cart submission itself is handled entirely by Dawn's own
     <product-form> element (product-form.js) — this only ever swaps which
     variant id that form will submit.
     --------------------------------------------------------------------- */

  if (!window.customElements.get('pampa-sample-card')) {
    window.customElements.define(
      'pampa-sample-card',
      class PampaSampleCard extends HTMLElement {
        connectedCallback() {
          this.select = this.querySelector('.pampa-samples-card__material-select');
          this.variantInput = this.querySelector('.pampa-samples-card__variant-id');
          this.submitButton = this.querySelector('.pampa-samples-card__submit');
          this.priceEl = this.querySelector('.pampa-samples-card__price');
          this.availabilityNote = this.querySelector('.pampa-samples-card__unavailable');

          if (this.select) {
            this.select.addEventListener('change', this.handleMaterialChange.bind(this));
          }
        }

        handleMaterialChange() {
          var option = this.select.options[this.select.selectedIndex];
          if (!option) return;

          var variantId = option.value;
          var available = option.getAttribute('data-available') === 'true';
          var priceHtml = option.getAttribute('data-price-html') || '';

          if (this.variantInput) {
            this.variantInput.value = variantId;
            this.variantInput.disabled = !available;
          }

          if (this.priceEl && priceHtml) this.priceEl.innerHTML = priceHtml;

          if (this.submitButton) {
            var label = this.submitButton.querySelector('span');
            if (!available) {
              this.submitButton.setAttribute('disabled', 'disabled');
              if (label) label.textContent = this.submitButton.getAttribute('data-sold-out-label') || 'Sold out';
            } else {
              this.submitButton.removeAttribute('disabled');
              this.submitButton.removeAttribute('aria-disabled');
              if (label) label.textContent = this.submitButton.getAttribute('data-add-label') || 'Add to tray';
            }
          }
        }
      }
    );
  }
})();
